import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveSeo, buildSiteOrigin, SITE_NAME, type Lang } from './seoData';

const LOCALE_MAP: Record<Lang, string> = {
  en: 'en_US',
  pt: 'pt_BR',
  de: 'de_DE',
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const meta = resolveSeo(location.pathname);
    const origin = buildSiteOrigin();
    const canonical = `${origin}${meta.canonicalPath}`;
    const locale = LOCALE_MAP[meta.lang];

    // Core document metadata
    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'keywords', meta.keywords);
    upsertCanonical(canonical);

    // Open Graph
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:type', meta.ogType);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', meta.image);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', locale);

    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertMeta('name', 'twitter:image', meta.image);
  }, [location.pathname]);

  return null;
}